import React, { useEffect, useState } from 'react';
import {
    useCurrencies,
    useGetPayoutSettings,
    useUpdatePayoutSettings,
} from '../../../types/generated';
import { FullscreenLoader } from '../../../components/FullscreenLoader/FullscreenLoader';
import { PayoutSettingsView } from './view';
import { Snackbar, SnackbarContent } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { useStyles } from '../../App/css';

export const PayoutSettings = (): JSX.Element => {
    const [snackbar, setSnackbar] = useState<
        { type: 'error' | 'success'; message: string } | undefined
    >(undefined);
    const classes = useStyles();
    const { data, loading, refetch } = useGetPayoutSettings();
    const [updatePayoutSettings, { loading: saveLoading }] = useUpdatePayoutSettings();
    const { data: currencies, loading: loadingCurrencies } = useCurrencies();

    useEffect(() => {
        refetch();
    }, [refetch]);

    const onSubmit = (values, form, callback) => {
        const isUsResident = values.usResident === 'true';

        updatePayoutSettings({
            variables: {
                payoutSettings: {
                    bankAccountNumber: values.bankAccountNumber,
                    bankName: values.bankName,
                    currencyCode: values.currencyCode,
                    isUsResident,
                    payableTo: values.payableTo,
                    paymentTerms: values.paymentTerms,
                    paymentType: values.paymentType,
                    payoutContactEmail: values.payoutContactEmail,
                    payoutContactPhoneNumber: values.payoutContactPhoneNumber,
                    nonUSBankInput: isUsResident
                        ? null
                        : {
                              swift: values.swift,
                              iban: values.iban,
                              branchId: values.branchId,
                              bankId: values.bankId,
                          },
                    usBankInput: isUsResident
                        ? {
                              abaRouting: values.abaRouting,
                          }
                        : null,
                },
            },
        })
            .then(() => {
                callback();
                setSnackbar({ type: 'success', message: 'Payout settings saved!' });
            })
            .catch(err => {
                callback(err);
                setSnackbar({
                    type: 'error',
                    message:
                        'Something went wrong with updating the payout settings, please try again.',
                });
            });
    };

    if (loading || loadingCurrencies) {
        return <FullscreenLoader />;
    }

    return (
        <PayoutSettingsView
            loading={saveLoading || loading || loadingCurrencies}
            onSubmit={onSubmit}
            initialValues={data.getPayoutSettings || undefined}
            currencyOptions={
                currencies && currencies.GetCurrencies.length > 0
                    ? currencies.GetCurrencies.map(c => ({
                          code: c.value,
                          name: c.value,
                      }))
                    : [
                          { code: 'ISK', name: 'ISK' },
                          { code: 'USD', name: 'USD' },
                      ]
            }
            snackbar={
                (snackbar && (
                    <Snackbar
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        open={Boolean(snackbar)}
                        onClose={() => {
                            setSnackbar(undefined);
                        }}
                        className={classes.errorSnackbar}
                    >
                        <SnackbarContent
                            className={
                                snackbar.type === 'error'
                                    ? classes.errorContent
                                    : classes.successContent
                            }
                            message={
                                <span id="error-snackbar" className={classes.errorMessage}>
                                    <ErrorIcon className={classes.errorIcon} />
                                    {snackbar.message}
                                </span>
                            }
                            action={[
                                <IconButton
                                    key="close"
                                    aria-label="close"
                                    color="inherit"
                                    onClick={() => {
                                        setSnackbar(undefined);
                                    }}
                                >
                                    <CloseIcon className={classes.errorCloseIcon} />
                                </IconButton>,
                            ]}
                        />
                    </Snackbar>
                )) ||
                undefined
            }
        />
    );
};
